import { Article, PlanfactOperation } from '../services/api';
import { isOperationExpense, isOperationReceipt, normalizeOperationAmount } from './operationBudgetArticle';
import {
  operationDealMatchesContractId,
  PLANFACT_MARGIN_CATEGORY_EXCLUSIONS,
} from './contractReceipts';

export const TASK_BALANCE_TAX_CATEGORY_NAME = 'Налог';
export const TASK_BALANCE_SERV_FEE_CATEGORY_NAME = 'Сервисный сбор';
export const TASK_BALANCE_CASH_CATEGORY_NAME = 'Кэш';

export const TASK_BALANCE_INCOME_ROW_ID = 'income';
export const TASK_BALANCE_EXPENSES_ROW_ID = 'expenses_total';
export const TASK_BALANCE_PERIOD_ROW_ID = 'period_balance';
export const TASK_BALANCE_CUMULATIVE_ROW_ID = 'cumulative_balance';
export const TASK_EXPENSES_PERIOD_ROW_ID = 'period_total';
export const TASK_EXPENSES_CUMULATIVE_ROW_ID = 'cumulative_total';
export const TASK_BALANCE_PARTNER_COMMISSION_ROW_ID = 'partner_commission';

export interface TaskBalanceExpenseCategory {
  id: string;
  label: string;
  /** Значения поля category в мастерданных «Статьи». */
  masterCategories: string[];
}

export const TASK_BALANCE_EXPENSE_CATEGORIES: TaskBalanceExpenseCategory[] = [
  {
    id: 'materials',
    label: 'Материалы',
    masterCategories: ['1_Материалы и доставка', '1_Материалы'],
  },
  { id: 'company_works', label: 'Работы компании', masterCategories: ['2_Работы компании'] },
  { id: 'equipment_rental', label: 'Аренда техники', masterCategories: ['3_Аренда техники'] },
  { id: 'third_parties', label: 'Третьи лица', masterCategories: ['4_Третьи лица'] },
  { id: 'tax', label: 'Налог', masterCategories: [] },
  { id: 'service_fee', label: 'Сервисный сбор', masterCategories: [] },
  { id: 'cash', label: 'Кэш', masterCategories: [] },
  { id: 'unallocated', label: 'Не распределено', masterCategories: ['5_Не распределено'] },
  {
    id: 'partner_commission',
    label: 'Комиссия партнера',
    masterCategories: ['5_Комиссия партнера', '6_Комиссия партнера'],
  },
];

export type TaskBalanceColumnKey = 'total' | 'before' | `month:${number}-${number}`;

export interface TaskBalanceColumn {
  key: TaskBalanceColumnKey;
  label: string;
}

export type TaskBalanceRowKind = 'income' | 'expense_total' | 'expense' | 'balance' | 'cumulative';

export interface TaskBalanceRow {
  id: string;
  label: string;
  kind: TaskBalanceRowKind;
  values: Record<TaskBalanceColumnKey, number>;
  /** План по строке; null — заглушка, пока нет данных планирования. */
  planValue?: number | null;
}

export type TaskBalanceTableVariant = 'cashflow' | 'expenses';

export interface TaskExpenseExtrasVisibility {
  cash: boolean;
  tax: boolean;
  serviceFee: boolean;
  partnerCommission: boolean;
}

export const ALL_TASK_EXPENSE_EXTRAS: TaskExpenseExtrasVisibility = {
  cash: true,
  tax: true,
  serviceFee: true,
  partnerCommission: true,
};

const SPECIAL_EXPENSE_ROW_IDS = new Set([
  'tax',
  'service_fee',
  'cash',
  TASK_BALANCE_PARTNER_COMMISSION_ROW_ID,
]);

/** null — все обычные статьи; Set — только эти planfact_article. */
export type TaskExpenseArticleFilter = Set<string> | null;

export interface TaskBalanceTableData {
  columns: TaskBalanceColumn[];
  rows: TaskBalanceRow[];
  variant?: TaskBalanceTableVariant;
}

interface BalancePeriodLayout {
  columns: TaskBalanceColumn[];
  monthKeys: Set<TaskBalanceColumnKey>;
  newestMonthKey: TaskBalanceColumnKey;
  windowStartMs: number;
}

function isPlanfactMarginCategory(categoryName: string | undefined): boolean {
  return categoryName != null && PLANFACT_MARGIN_CATEGORY_EXCLUSIONS.has(categoryName);
}

function parseOperationDate(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const ms = new Date(`${trimmed}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : new Date(ms);
}

function monthColumnKey(year: number, month: number): TaskBalanceColumnKey {
  return `month:${year}-${month}`;
}

const MONTH_SHORT_LABELS_RU = [
  'Янв',
  'Фев',
  'Мар',
  'Апр',
  'Май',
  'Июн',
  'Июл',
  'Авг',
  'Сен',
  'Окт',
  'Ноя',
  'Дек',
];

function formatMonthLabel(year: number, month: number): string {
  const yy = year % 100;
  const monthName = MONTH_SHORT_LABELS_RU[month - 1] ?? String(month);
  return `${monthName}-${String(yy).padStart(2, '0')}`;
}

function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  let y = year;
  let m = month + delta;
  while (m < 1) {
    m += 12;
    y -= 1;
  }
  while (m > 12) {
    m -= 12;
    y += 1;
  }
  return { year: y, month: m };
}

function startOfMonthMs(year: number, month: number): number {
  return new Date(year, month - 1, 1).getTime();
}

function buildPlanfactArticleToCategoryMap(articles: Article[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const article of articles) {
    const planfactArticle = article.planfact_article?.trim();
    const category = article.category?.trim();
    if (planfactArticle && category) {
      map.set(planfactArticle, category);
    }
  }
  return map;
}

function findExpenseCategoryByMasterCategory(masterCategory: string): TaskBalanceExpenseCategory | undefined {
  return TASK_BALANCE_EXPENSE_CATEGORIES.find((row) =>
    row.masterCategories.includes(masterCategory),
  );
}

function resolveExpenseRowId(
  categoryName: string | undefined,
  articleToCategory: Map<string, string>,
): string {
  const name = categoryName?.trim();
  if (!name) {
    return 'unallocated';
  }
  if (name === TASK_BALANCE_TAX_CATEGORY_NAME) {
    return 'tax';
  }
  if (name === TASK_BALANCE_SERV_FEE_CATEGORY_NAME) {
    return 'service_fee';
  }
  if (name === TASK_BALANCE_CASH_CATEGORY_NAME) {
    return 'cash';
  }

  const masterCategory = articleToCategory.get(name);
  if (masterCategory) {
    const matched = findExpenseCategoryByMasterCategory(masterCategory);
    if (matched) {
      return matched.id;
    }
    return 'unallocated';
  }

  const directMatch = findExpenseCategoryByMasterCategory(name)
    ?? TASK_BALANCE_EXPENSE_CATEGORIES.find((row) => row.label === name);
  if (directMatch) {
    return directMatch.id;
  }

  return 'unallocated';
}

function hiddenExpenseRowIds(extras: TaskExpenseExtrasVisibility): Set<string> {
  const hidden = new Set<string>();
  if (!extras.cash) hidden.add('cash');
  if (!extras.tax) hidden.add('tax');
  if (!extras.serviceFee) hidden.add('service_fee');
  if (!extras.partnerCommission) hidden.add(TASK_BALANCE_PARTNER_COMMISSION_ROW_ID);
  return hidden;
}

function allowedRegularExpenseRowIds(
  allowedPlanfactArticles: TaskExpenseArticleFilter,
  articles: Article[],
): Set<string> | null {
  if (allowedPlanfactArticles == null) {
    return null;
  }
  const articleToCategory = buildPlanfactArticleToCategoryMap(articles);
  const ids = new Set<string>();
  for (const name of allowedPlanfactArticles) {
    ids.add(resolveExpenseRowId(name, articleToCategory));
  }
  return ids;
}

export function operationContributesExpenseExtras(
  operation: PlanfactOperation,
  extras: TaskExpenseExtrasVisibility,
): boolean {
  if (extras.tax && operation.tax_amount != null && operation.tax_amount !== 0) {
    return true;
  }
  if (extras.serviceFee && operation.serv_fee_amount != null && operation.serv_fee_amount !== 0) {
    return true;
  }
  if (extras.cash && operation.cash_amount != null && operation.cash_amount !== 0) {
    return true;
  }
  return false;
}

function isRegularExpenseOperationVisible(
  operation: PlanfactOperation,
  articleToCategory: Map<string, string>,
  extras: TaskExpenseExtrasVisibility,
  allowedPlanfactArticles: TaskExpenseArticleFilter,
): boolean {
  const rowId = resolveExpenseRowId(operation.category_name, articleToCategory);
  if (rowId === 'tax') {
    return extras.tax;
  }
  if (rowId === 'service_fee') {
    return extras.serviceFee;
  }
  if (rowId === 'cash') {
    return extras.cash;
  }
  if (rowId === TASK_BALANCE_PARTNER_COMMISSION_ROW_ID) {
    return extras.partnerCommission;
  }
  if (allowedPlanfactArticles == null) {
    return true;
  }
  const name = operation.category_name?.trim();
  return Boolean(name && allowedPlanfactArticles.has(name));
}

function visibleExpenseDetailRows(
  expenseValuesById: Map<string, Record<TaskBalanceColumnKey, number>>,
  columns: TaskBalanceColumn[],
  extras: TaskExpenseExtrasVisibility,
  allowedPlanfactArticles: TaskExpenseArticleFilter = null,
  articles: Article[] = [],
): TaskBalanceRow[] {
  const hiddenIds = hiddenExpenseRowIds(extras);
  const allowedRegularIds = allowedRegularExpenseRowIds(allowedPlanfactArticles, articles);
  return TASK_BALANCE_EXPENSE_CATEGORIES.filter((category) => {
    if (hiddenIds.has(category.id)) {
      return false;
    }
    if (SPECIAL_EXPENSE_ROW_IDS.has(category.id)) {
      return true;
    }
    return allowedRegularIds == null || allowedRegularIds.has(category.id);
  }).map((category) => ({
    id: category.id,
    label: category.label,
    kind: 'expense' as const,
    values: expenseValuesById.get(category.id) ?? createEmptyValues(columns),
  }));
}

export function excludeHiddenExpenseCategoryOperations(
  operations: PlanfactOperation[],
  articles: Article[],
  extras: TaskExpenseExtrasVisibility,
  allowedPlanfactArticles: TaskExpenseArticleFilter = null,
): PlanfactOperation[] {
  const articleToCategory = buildPlanfactArticleToCategoryMap(articles);
  return operations.filter((operation) => {
    if (!isOperationExpense(operation)) {
      return true;
    }
    return isRegularExpenseOperationVisible(
      operation,
      articleToCategory,
      extras,
      allowedPlanfactArticles,
    );
  });
}

const TASK_BALANCE_MONTH_COUNT = 12;
const TASK_EXPENSES_MONTH_COUNT = 6;

function buildPeriodLayout(
  lastExpenseDate: Date | null,
  monthCount: number = TASK_BALANCE_MONTH_COUNT,
): BalancePeriodLayout {
  const anchor = lastExpenseDate ?? new Date();
  const anchorYear = anchor.getFullYear();
  const anchorMonth = anchor.getMonth() + 1;
  const visibleMonths = Math.max(1, monthCount);

  const monthColumns: TaskBalanceColumn[] = [];
  const monthKeys = new Set<TaskBalanceColumnKey>();
  let year = anchorYear;
  let month = anchorMonth;

  for (let index = 0; index < visibleMonths; index += 1) {
    const key = monthColumnKey(year, month);
    monthColumns.push({ key, label: formatMonthLabel(year, month) });
    monthKeys.add(key);
    if (index === visibleMonths - 1) {
      break;
    }
    const prev = shiftMonth(year, month, -1);
    year = prev.year;
    month = prev.month;
  }

  const oldest = monthColumns[monthColumns.length - 1];
  const oldestMatch = /^month:(\d+)-(\d+)$/.exec(oldest.key);
  const oldestYear = oldestMatch ? Number(oldestMatch[1]) : anchorYear;
  const oldestMonth = oldestMatch ? Number(oldestMatch[2]) : anchorMonth;

  const columns: TaskBalanceColumn[] = [
    { key: 'total', label: 'ИТОГО' },
    ...monthColumns,
    { key: 'before', label: `до ${oldest.label}` },
  ];

  return {
    columns,
    monthKeys,
    newestMonthKey: monthColumns[0]?.key ?? monthColumnKey(anchorYear, anchorMonth),
    windowStartMs: startOfMonthMs(oldestYear, oldestMonth),
  };
}

function assignPeriodKey(date: Date, layout: BalancePeriodLayout): TaskBalanceColumnKey {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const key = monthColumnKey(year, month);
  if (layout.monthKeys.has(key)) {
    return key;
  }
  if (date.getTime() < layout.windowStartMs) {
    return 'before';
  }
  return layout.newestMonthKey;
}

function createEmptyValues(columns: TaskBalanceColumn[]): Record<TaskBalanceColumnKey, number> {
  const values = {} as Record<TaskBalanceColumnKey, number>;
  for (const column of columns) {
    values[column.key] = 0;
  }
  return values;
}

function addToCell(
  values: Record<TaskBalanceColumnKey, number>,
  key: TaskBalanceColumnKey,
  amount: number,
): void {
  values[key] = (values[key] ?? 0) + amount;
  values.total = (values.total ?? 0) + amount;
}

function sumRowsByColumn(
  rows: TaskBalanceRow[],
  columns: TaskBalanceColumn[],
): Record<TaskBalanceColumnKey, number> {
  const totals = createEmptyValues(columns);
  for (const row of rows) {
    for (const column of columns) {
      totals[column.key] += row.values[column.key] ?? 0;
    }
  }
  return totals;
}

function buildCumulativeBalance(
  periodValues: Record<TaskBalanceColumnKey, number>,
  columns: TaskBalanceColumn[],
): Record<TaskBalanceColumnKey, number> {
  const cumulative = createEmptyValues(columns);
  const periodKeys = columns
    .map((column) => column.key)
    .filter((key): key is TaskBalanceColumnKey => key !== 'total')
    .reverse();

  let running = 0;
  for (const key of periodKeys) {
    running += periodValues[key] ?? 0;
    cumulative[key] = running;
  }
  cumulative.total = running;
  return cumulative;
}

export function filterTaskOperations(
  operations: PlanfactOperation[],
  businessTaskId: string,
): PlanfactOperation[] {
  const normalizedId = businessTaskId.trim();
  if (!normalizedId) {
    return [];
  }
  return operations.filter((operation) => operation.project_name?.trim() === normalizedId);
}

/** Периоды слева направо в хронологии (для накопительного баланса). */
function chronologicalPeriodKeys(columns: TaskBalanceColumn[]): TaskBalanceColumnKey[] {
  return columns
    .map((column) => column.key)
    .filter((key): key is TaskBalanceColumnKey => key !== 'total')
    .reverse();
}

function periodKeysUpTo(
  columns: TaskBalanceColumn[],
  columnKey: TaskBalanceColumnKey,
): Set<TaskBalanceColumnKey> {
  if (columnKey === 'total') {
    return new Set(chronologicalPeriodKeys(columns));
  }
  const ordered = chronologicalPeriodKeys(columns);
  const result = new Set<TaskBalanceColumnKey>();
  for (const key of ordered) {
    result.add(key);
    if (key === columnKey) {
      break;
    }
  }
  return result;
}

function isCumulativeBalanceRow(rowId: string): boolean {
  return rowId === TASK_BALANCE_CUMULATIVE_ROW_ID || rowId === TASK_EXPENSES_CUMULATIVE_ROW_ID;
}

function isPeriodOrCumulativeBalanceRow(rowId: string): boolean {
  return (
    rowId === TASK_BALANCE_PERIOD_ROW_ID
    || rowId === TASK_EXPENSES_PERIOD_ROW_ID
    || isCumulativeBalanceRow(rowId)
  );
}

/**
 * Операции, составляющие значение ячейки таблицы баланса
 * (та же логика отбора, что в buildTaskBalanceTable).
 */
export function collectOperationsForBalanceCell(params: {
  operations: PlanfactOperation[];
  articles: Article[];
  businessTaskId: string;
  rowId: string;
  columnKey: TaskBalanceColumnKey;
  includeReceipts?: boolean;
  extras?: TaskExpenseExtrasVisibility;
  allowedPlanfactArticles?: TaskExpenseArticleFilter;
  monthCount?: number;
}): PlanfactOperation[] {
  const {
    operations,
    articles,
    businessTaskId,
    rowId,
    columnKey,
    includeReceipts = true,
    extras = ALL_TASK_EXPENSE_EXTRAS,
    allowedPlanfactArticles = null,
    monthCount = TASK_BALANCE_MONTH_COUNT,
  } = params;
  const taskOperations = filterTaskOperations(operations, businessTaskId);
  const articleToCategory = buildPlanfactArticleToCategoryMap(articles);
  const layout = buildPeriodLayout(findLastExpenseDate(taskOperations), monthCount);
  const matchedPeriods =
    columnKey === 'total'
      ? null
      : isCumulativeBalanceRow(rowId)
        ? periodKeysUpTo(layout.columns, columnKey)
        : new Set<TaskBalanceColumnKey>([columnKey]);

  const matched: PlanfactOperation[] = [];
  const seen = new Set<number>();

  const pushOp = (operation: PlanfactOperation): void => {
    if (seen.has(operation.id)) return;
    seen.add(operation.id);
    matched.push(operation);
  };

  for (const operation of taskOperations) {
    const date = parseOperationDate(operation.operation_date);
    if (!date) continue;
    const contributesTax =
      extras.tax && operation.tax_amount != null && operation.tax_amount !== 0;
    const contributesServiceFee =
      extras.serviceFee && operation.serv_fee_amount != null && operation.serv_fee_amount !== 0;
    const contributesCash =
      extras.cash && operation.cash_amount != null && operation.cash_amount !== 0;
    if (
      !includeReceipts
      && !isOperationExpense(operation)
      && !contributesTax
      && !contributesServiceFee
      && !contributesCash
    ) {
      continue;
    }
    const periodKey = assignPeriodKey(date, layout);
    if (matchedPeriods != null && !matchedPeriods.has(periodKey)) {
      continue;
    }

    if (rowId === 'tax' && contributesTax) {
      pushOp(operation);
      continue;
    }
    if (rowId === 'service_fee' && contributesServiceFee) {
      pushOp(operation);
      continue;
    }
    if (rowId === 'cash' && contributesCash) {
      pushOp(operation);
      continue;
    }

    const amount = normalizeOperationAmount(operation.amount, operation.operation_type);
    const isReceipt = amount !== 0 && isOperationReceipt(operation);
    const isExpenseOp =
      amount !== 0
      && isOperationExpense(operation)
      && !isPlanfactMarginCategory(operation.category_name);
    const expenseRowId = isExpenseOp
      ? resolveExpenseRowId(operation.category_name, articleToCategory)
      : null;
    const isVisibleExpenseOp =
      isExpenseOp
      && isRegularExpenseOperationVisible(
        operation,
        articleToCategory,
        extras,
        allowedPlanfactArticles,
      );

    const contributesExpenseDetail =
      contributesTax
      || contributesServiceFee
      || contributesCash
      || isVisibleExpenseOp;

    if (includeReceipts && rowId === TASK_BALANCE_INCOME_ROW_ID && isReceipt) {
      pushOp(operation);
      continue;
    }

    if (rowId === TASK_BALANCE_EXPENSES_ROW_ID && contributesExpenseDetail) {
      pushOp(operation);
      continue;
    }

    if (
      TASK_BALANCE_EXPENSE_CATEGORIES.some((category) => category.id === rowId)
      && rowId !== 'tax'
      && rowId !== 'service_fee'
      && rowId !== 'cash'
      && isVisibleExpenseOp
      && expenseRowId === rowId
    ) {
      pushOp(operation);
      continue;
    }

    if (isPeriodOrCumulativeBalanceRow(rowId)) {
      if (contributesExpenseDetail || (includeReceipts && isReceipt)) {
        pushOp(operation);
      }
    }
  }

  return matched;
}

export function collectOperationsForExpensesCell(params: {
  operations: PlanfactOperation[];
  articles: Article[];
  businessTaskId: string;
  rowId: string;
  columnKey: TaskBalanceColumnKey;
  extras?: TaskExpenseExtrasVisibility;
  allowedPlanfactArticles?: TaskExpenseArticleFilter;
}): PlanfactOperation[] {
  return collectOperationsForBalanceCell({
    ...params,
    includeReceipts: false,
    monthCount: TASK_EXPENSES_MONTH_COUNT,
  });
}

function findLastExpenseDate(operations: PlanfactOperation[]): Date | null {
  let last: Date | null = null;
  for (const operation of operations) {
    const hasExtraAmount =
      (operation.tax_amount != null && operation.tax_amount !== 0)
      || (operation.serv_fee_amount != null && operation.serv_fee_amount !== 0)
      || (operation.cash_amount != null && operation.cash_amount !== 0);
    const hasExpenseBody =
      isOperationExpense(operation) && !isPlanfactMarginCategory(operation.category_name);
    if (!hasExpenseBody && !hasExtraAmount) {
      continue;
    }
    const date = parseOperationDate(operation.operation_date);
    if (!date) {
      continue;
    }
    if (!last || date.getTime() > last.getTime()) {
      last = date;
    }
  }
  return last;
}

export function buildTaskBalanceTable(params: {
  operations: PlanfactOperation[];
  articles: Article[];
  businessTaskId: string;
  extras?: TaskExpenseExtrasVisibility;
  allowedPlanfactArticles?: TaskExpenseArticleFilter;
  monthCount?: number;
}): TaskBalanceTableData {
  const extras = params.extras ?? ALL_TASK_EXPENSE_EXTRAS;
  const allowedPlanfactArticles = params.allowedPlanfactArticles ?? null;
  const monthCount = params.monthCount ?? TASK_BALANCE_MONTH_COUNT;
  const { operations, articles, businessTaskId } = params;
  const taskOperations = filterTaskOperations(operations, businessTaskId);
  const articleToCategory = buildPlanfactArticleToCategoryMap(articles);
  const layout = buildPeriodLayout(findLastExpenseDate(taskOperations), monthCount);
  const columns = layout.columns;

  const incomeValues = createEmptyValues(columns);
  const expenseValuesById = new Map<string, Record<TaskBalanceColumnKey, number>>();
  for (const category of TASK_BALANCE_EXPENSE_CATEGORIES) {
    expenseValuesById.set(category.id, createEmptyValues(columns));
  }

  for (const operation of taskOperations) {
    const date = parseOperationDate(operation.operation_date);
    if (!date) {
      continue;
    }
    const periodKey = assignPeriodKey(date, layout);

    if (operation.tax_amount != null && operation.tax_amount !== 0) {
      const taxRowValues = expenseValuesById.get('tax');
      if (taxRowValues) {
        addToCell(taxRowValues, periodKey, operation.tax_amount);
      }
    }

    if (operation.serv_fee_amount != null && operation.serv_fee_amount !== 0) {
      const serviceFeeRowValues = expenseValuesById.get('service_fee');
      if (serviceFeeRowValues) {
        addToCell(serviceFeeRowValues, periodKey, operation.serv_fee_amount);
      }
    }

    if (operation.cash_amount != null && operation.cash_amount !== 0) {
      const cashRowValues = expenseValuesById.get('cash');
      if (cashRowValues) {
        addToCell(cashRowValues, periodKey, operation.cash_amount);
      }
    }

    const amount = normalizeOperationAmount(operation.amount, operation.operation_type);
    if (amount === 0) {
      continue;
    }

    if (isOperationReceipt(operation)) {
      addToCell(incomeValues, periodKey, amount);
      continue;
    }

    if (!isOperationExpense(operation)) {
      continue;
    }
    if (isPlanfactMarginCategory(operation.category_name)) {
      continue;
    }
    if (
      !isRegularExpenseOperationVisible(
        operation,
        articleToCategory,
        extras,
        allowedPlanfactArticles,
      )
    ) {
      continue;
    }

    const expenseRowId = resolveExpenseRowId(operation.category_name, articleToCategory);
    const rowValues = expenseValuesById.get(expenseRowId) ?? expenseValuesById.get('unallocated');
    if (rowValues) {
      addToCell(rowValues, periodKey, amount);
    }
  }

  const expenseDetailRows = visibleExpenseDetailRows(
    expenseValuesById,
    columns,
    extras,
    allowedPlanfactArticles,
    articles,
  );
  const expensesTotalValues = sumRowsByColumn(expenseDetailRows, columns);

  const periodValues = createEmptyValues(columns);
  for (const column of columns) {
    const key = column.key;
    periodValues[key] = (incomeValues[key] ?? 0) + (expensesTotalValues[key] ?? 0);
  }

  const cumulativeValues = buildCumulativeBalance(periodValues, columns);

  const rows: TaskBalanceRow[] = [
    { id: TASK_BALANCE_INCOME_ROW_ID, label: 'ПОСТУПЛЕНИЯ', kind: 'income', values: incomeValues },
    {
      id: TASK_BALANCE_EXPENSES_ROW_ID,
      label: 'РАСХОДЫ',
      kind: 'expense_total',
      values: expensesTotalValues,
    },
    ...expenseDetailRows,
    {
      id: TASK_BALANCE_PERIOD_ROW_ID,
      label: 'ДЕН. ПОТОК периода',
      kind: 'balance',
      values: periodValues,
    },
    {
      id: TASK_BALANCE_CUMULATIVE_ROW_ID,
      label: 'ДЕН. ПОТОК накопительный',
      kind: 'cumulative',
      values: cumulativeValues,
    },
  ];

  return { columns, rows };
}

export function buildTaskExpensesTable(params: {
  operations: PlanfactOperation[];
  articles: Article[];
  businessTaskId: string;
  extras?: TaskExpenseExtrasVisibility;
  allowedPlanfactArticles?: TaskExpenseArticleFilter;
}): TaskBalanceTableData {
  const extras = params.extras ?? ALL_TASK_EXPENSE_EXTRAS;
  const { columns, rows } = buildTaskBalanceTable({
    ...params,
    extras,
    monthCount: TASK_EXPENSES_MONTH_COUNT,
  });
  const hiddenIds = hiddenExpenseRowIds(extras);

  const expenseDetailRows = rows.filter(
    (row) => row.kind === 'expense' && !hiddenIds.has(row.id),
  );
  const expensesTotalValues = sumRowsByColumn(expenseDetailRows, columns);
  const cumulativeValues = buildCumulativeBalance(expensesTotalValues, columns);

  const withStubPlan = (row: TaskBalanceRow): TaskBalanceRow => ({
    ...row,
    planValue: null,
  });

  return {
    variant: 'expenses',
    columns: columns.map((column) =>
      column.key === 'total' ? { ...column, label: 'Факт' } : column,
    ),
    rows: [
      withStubPlan({
        id: TASK_BALANCE_EXPENSES_ROW_ID,
        label: 'РАСХОДЫ',
        kind: 'expense_total',
        values: expensesTotalValues,
      }),
      ...expenseDetailRows.map(withStubPlan),
      withStubPlan({
        id: TASK_EXPENSES_PERIOD_ROW_ID,
        label: 'ИТОГО периода',
        kind: 'balance',
        values: expensesTotalValues,
      }),
      withStubPlan({
        id: TASK_EXPENSES_CUMULATIVE_ROW_ID,
        label: 'ИТОГО накопительный',
        kind: 'cumulative',
        values: cumulativeValues,
      }),
    ],
  };
}

function normalizeFamilyTaskIds(familyTaskIds: Iterable<string>): Set<string> {
  const taskIds = new Set<string>();
  for (const raw of familyTaskIds) {
    const trimmed = raw.trim();
    if (trimmed) {
      taskIds.add(trimmed);
    }
  }
  return taskIds;
}

function normalizeFamilyContractIds(familyContractIds: Iterable<number>): Set<number> {
  return new Set(familyContractIds);
}

function operationMatchesFamilyDeal(
  operation: PlanfactOperation,
  familyContractIds: Set<number>,
): boolean {
  for (const contractId of familyContractIds) {
    if (operationDealMatchesContractId(operation.deal_name, contractId)) {
      return true;
    }
  }
  return false;
}

function operationMatchesFamilyTask(
  operation: PlanfactOperation,
  familyTaskIds: Set<string>,
): boolean {
  const projectName = operation.project_name?.trim();
  return Boolean(projectName && familyTaskIds.has(projectName));
}

/**
 * Операции семьи договоров: поступления по названию сделки (deal_name),
 * расходы — по ID задач (project_name).
 */
export function filterContractFamilyOperations(
  operations: PlanfactOperation[],
  familyContractIds: Iterable<number>,
  familyTaskIds: Iterable<string>,
): PlanfactOperation[] {
  const contractIds = normalizeFamilyContractIds(familyContractIds);
  const taskIds = normalizeFamilyTaskIds(familyTaskIds);
  if (contractIds.size === 0 && taskIds.size === 0) {
    return [];
  }
  return operations.filter(
    (operation) =>
      operationMatchesFamilyDeal(operation, contractIds)
      || operationMatchesFamilyTask(operation, taskIds),
  );
}

function findLastFamilyExpenseDate(
  operations: PlanfactOperation[],
  familyTaskIds: Set<string>,
): Date | null {
  let last: Date | null = null;
  for (const operation of operations) {
    if (!operationMatchesFamilyTask(operation, familyTaskIds)) {
      continue;
    }
    if (!isOperationExpense(operation)) {
      continue;
    }
    if (isPlanfactMarginCategory(operation.category_name)) {
      continue;
    }
    const date = parseOperationDate(operation.operation_date);
    if (!date) {
      continue;
    }
    if (!last || date.getTime() > last.getTime()) {
      last = date;
    }
  }
  return last;
}

/**
 * Таблица баланса по семье договоров:
 * поступления — по названию сделки, расходы — по ID задач.
 */
export function buildContractFamilyBalanceTable(params: {
  operations: PlanfactOperation[];
  articles: Article[];
  familyContractIds: Iterable<number>;
  familyTaskIds: Iterable<string>;
  extras?: TaskExpenseExtrasVisibility;
}): TaskBalanceTableData {
  const extras = params.extras ?? ALL_TASK_EXPENSE_EXTRAS;
  const { operations, articles } = params;
  const familyContractIds = normalizeFamilyContractIds(params.familyContractIds);
  const familyTaskIds = normalizeFamilyTaskIds(params.familyTaskIds);
  const articleToCategory = buildPlanfactArticleToCategoryMap(articles);
  const layout = buildPeriodLayout(findLastFamilyExpenseDate(operations, familyTaskIds));
  const columns = layout.columns;

  const incomeValues = createEmptyValues(columns);
  const expenseValuesById = new Map<string, Record<TaskBalanceColumnKey, number>>();
  for (const category of TASK_BALANCE_EXPENSE_CATEGORIES) {
    expenseValuesById.set(category.id, createEmptyValues(columns));
  }

  for (const operation of operations) {
    const date = parseOperationDate(operation.operation_date);
    if (!date) {
      continue;
    }
    const periodKey = assignPeriodKey(date, layout);
    const isDealMatch = operationMatchesFamilyDeal(operation, familyContractIds);
    const isTaskMatch = operationMatchesFamilyTask(operation, familyTaskIds);

    if (isTaskMatch) {
      if (operation.tax_amount != null && operation.tax_amount !== 0) {
        const taxRowValues = expenseValuesById.get('tax');
        if (taxRowValues) {
          addToCell(taxRowValues, periodKey, operation.tax_amount);
        }
      }

      if (operation.serv_fee_amount != null && operation.serv_fee_amount !== 0) {
        const serviceFeeRowValues = expenseValuesById.get('service_fee');
        if (serviceFeeRowValues) {
          addToCell(serviceFeeRowValues, periodKey, operation.serv_fee_amount);
        }
      }

      if (operation.cash_amount != null && operation.cash_amount !== 0) {
        const cashRowValues = expenseValuesById.get('cash');
        if (cashRowValues) {
          addToCell(cashRowValues, periodKey, operation.cash_amount);
        }
      }
    }

    const amount = normalizeOperationAmount(operation.amount, operation.operation_type);
    if (amount === 0) {
      continue;
    }

    if (isDealMatch && isOperationReceipt(operation)) {
      addToCell(incomeValues, periodKey, amount);
      continue;
    }

    if (!isTaskMatch || !isOperationExpense(operation)) {
      continue;
    }
    if (isPlanfactMarginCategory(operation.category_name)) {
      continue;
    }

    const expenseRowId = resolveExpenseRowId(operation.category_name, articleToCategory);
    const rowValues = expenseValuesById.get(expenseRowId) ?? expenseValuesById.get('unallocated');
    if (rowValues) {
      addToCell(rowValues, periodKey, amount);
    }
  }

  const expenseDetailRows = visibleExpenseDetailRows(expenseValuesById, columns, extras);
  const expensesTotalValues = sumRowsByColumn(expenseDetailRows, columns);

  const periodValues = createEmptyValues(columns);
  for (const column of columns) {
    const key = column.key;
    periodValues[key] = (incomeValues[key] ?? 0) + (expensesTotalValues[key] ?? 0);
  }

  const cumulativeValues = buildCumulativeBalance(periodValues, columns);

  const rows: TaskBalanceRow[] = [
    { id: TASK_BALANCE_INCOME_ROW_ID, label: 'ПОСТУПЛЕНИЯ', kind: 'income', values: incomeValues },
    {
      id: TASK_BALANCE_EXPENSES_ROW_ID,
      label: 'РАСХОДЫ',
      kind: 'expense_total',
      values: expensesTotalValues,
    },
    ...expenseDetailRows,
    {
      id: TASK_BALANCE_PERIOD_ROW_ID,
      label: 'ДЕН. ПОТОК периода',
      kind: 'balance',
      values: periodValues,
    },
    {
      id: TASK_BALANCE_CUMULATIVE_ROW_ID,
      label: 'ДЕН. ПОТОК накопительный',
      kind: 'cumulative',
      values: cumulativeValues,
    },
  ];

  return { columns, rows };
}

/**
 * Операции, составляющие значение ячейки баланса семьи договоров
 * (та же логика отбора, что в buildContractFamilyBalanceTable).
 */
export function collectOperationsForContractFamilyBalanceCell(params: {
  operations: PlanfactOperation[];
  articles: Article[];
  familyContractIds: Iterable<number>;
  familyTaskIds: Iterable<string>;
  rowId: string;
  columnKey: TaskBalanceColumnKey;
  extras?: TaskExpenseExtrasVisibility;
}): PlanfactOperation[] {
  const extras = params.extras ?? ALL_TASK_EXPENSE_EXTRAS;
  const { operations, articles, rowId, columnKey } = params;
  const familyContractIds = normalizeFamilyContractIds(params.familyContractIds);
  const familyTaskIds = normalizeFamilyTaskIds(params.familyTaskIds);
  const articleToCategory = buildPlanfactArticleToCategoryMap(articles);
  const layout = buildPeriodLayout(findLastFamilyExpenseDate(operations, familyTaskIds));
  const matchedPeriods =
    columnKey === 'total'
      ? null
      : rowId === TASK_BALANCE_CUMULATIVE_ROW_ID
        ? periodKeysUpTo(layout.columns, columnKey)
        : new Set<TaskBalanceColumnKey>([columnKey]);

  const matched: PlanfactOperation[] = [];
  const seen = new Set<number>();

  const pushOp = (operation: PlanfactOperation): void => {
    if (seen.has(operation.id)) return;
    seen.add(operation.id);
    matched.push(operation);
  };

  for (const operation of operations) {
    const date = parseOperationDate(operation.operation_date);
    if (!date) continue;
    const periodKey = assignPeriodKey(date, layout);
    if (matchedPeriods != null && !matchedPeriods.has(periodKey)) {
      continue;
    }

    const isDealMatch = operationMatchesFamilyDeal(operation, familyContractIds);
    const isTaskMatch = operationMatchesFamilyTask(operation, familyTaskIds);

    const contributesTax =
      isTaskMatch && operation.tax_amount != null && operation.tax_amount !== 0;
    const contributesServiceFee =
      isTaskMatch && operation.serv_fee_amount != null && operation.serv_fee_amount !== 0;
    const contributesCash =
      isTaskMatch && operation.cash_amount != null && operation.cash_amount !== 0;

    if (rowId === 'tax' && contributesTax) {
      pushOp(operation);
      continue;
    }
    if (rowId === 'service_fee' && contributesServiceFee) {
      pushOp(operation);
      continue;
    }
    if (rowId === 'cash' && contributesCash) {
      pushOp(operation);
      continue;
    }

    const amount = normalizeOperationAmount(operation.amount, operation.operation_type);
    const isReceipt = amount !== 0 && isDealMatch && isOperationReceipt(operation);
    const isExpenseOp =
      amount !== 0
      && isTaskMatch
      && isOperationExpense(operation)
      && !isPlanfactMarginCategory(operation.category_name);
    const expenseRowId = isExpenseOp
      ? resolveExpenseRowId(operation.category_name, articleToCategory)
      : null;
    const isVisibleExpenseOp =
      isExpenseOp
      && (
        extras.partnerCommission
        || expenseRowId !== TASK_BALANCE_PARTNER_COMMISSION_ROW_ID
      );

    const contributesExpenseDetail =
      contributesTax
      || contributesServiceFee
      || contributesCash
      || isVisibleExpenseOp;

    if (rowId === TASK_BALANCE_INCOME_ROW_ID && isReceipt) {
      pushOp(operation);
      continue;
    }

    if (rowId === TASK_BALANCE_EXPENSES_ROW_ID && contributesExpenseDetail) {
      pushOp(operation);
      continue;
    }

    if (
      TASK_BALANCE_EXPENSE_CATEGORIES.some((category) => category.id === rowId)
      && rowId !== 'tax'
      && rowId !== 'service_fee'
      && rowId !== 'cash'
      && isVisibleExpenseOp
      && expenseRowId === rowId
    ) {
      pushOp(operation);
      continue;
    }

    if (
      (rowId === TASK_BALANCE_PERIOD_ROW_ID || rowId === TASK_BALANCE_CUMULATIVE_ROW_ID)
      && (isReceipt || contributesExpenseDetail)
    ) {
      pushOp(operation);
    }
  }

  return matched;
}
